// Pose-frame representation shared by the live camera, offline re-analysis and
// the simulator. Landmarks follow MediaPipe Pose Landmarker's 33-point layout,
// in normalized image coordinates (x right, y down, both 0..1).

export const LM = {
  nose: 0,
  leftEyeInner: 1,
  leftEye: 2,
  leftEyeOuter: 3,
  rightEyeInner: 4,
  rightEye: 5,
  rightEyeOuter: 6,
  leftEar: 7,
  rightEar: 8,
  mouthLeft: 9,
  mouthRight: 10,
  leftShoulder: 11,
  rightShoulder: 12,
  leftElbow: 13,
  rightElbow: 14,
  leftWrist: 15,
  rightWrist: 16,
  leftPinky: 17,
  rightPinky: 18,
  leftIndex: 19,
  rightIndex: 20,
  leftThumb: 21,
  rightThumb: 22,
  leftHip: 23,
  rightHip: 24,
  leftKnee: 25,
  rightKnee: 26,
  leftAnkle: 27,
  rightAnkle: 28,
  leftHeel: 29,
  rightHeel: 30,
  leftToe: 31, // MediaPipe "foot index"
  rightToe: 32,
} as const;

export const NUM_LANDMARKS = 33;
/** Floats stored per landmark: x, y, visibility. */
export const LM_STRIDE = 3;

/** Skeleton edges used for drawing. */
export const POSE_EDGES: ReadonlyArray<readonly [number, number]> = [
  [11, 12], [11, 23], [12, 24], [23, 24],
  [11, 13], [13, 15], [12, 14], [14, 16],
  [15, 19], [16, 20],
  [23, 25], [25, 27], [24, 26], [26, 28],
  [27, 29], [29, 31], [27, 31], [28, 30], [30, 32], [28, 32],
  [0, 7], [0, 8],
];

/**
 * One analysed frame. `t` is milliseconds on the attempt's own clock (0 = when
 * capture started). `lm` is null when no person was detected.
 */
export interface PoseFrame {
  t: number;
  lm: Float32Array | null;
}

export interface Timeline {
  frames: PoseFrame[];
  /** Pixel size of the analysed frames. */
  width: number;
  height: number;
}

export const lmX = (lm: Float32Array, i: number) => lm[i * LM_STRIDE];
export const lmY = (lm: Float32Array, i: number) => lm[i * LM_STRIDE + 1];
export const lmVis = (lm: Float32Array, i: number) => lm[i * LM_STRIDE + 2];

export function packLandmarks(points: ReadonlyArray<{ x: number; y: number; visibility?: number }>): Float32Array {
  const out = new Float32Array(NUM_LANDMARKS * LM_STRIDE);
  for (let i = 0; i < NUM_LANDMARKS; i++) {
    const p = points[i];
    out[i * LM_STRIDE] = p.x;
    out[i * LM_STRIDE + 1] = p.y;
    out[i * LM_STRIDE + 2] = p.visibility ?? 1;
  }
  return out;
}

// --- Compact storage: one Float32Array, per frame [t, present, 99 floats] ---

const FRAME_FLOATS = 2 + NUM_LANDMARKS * LM_STRIDE;

export function serializeFrames(frames: readonly PoseFrame[]): Float32Array {
  const out = new Float32Array(frames.length * FRAME_FLOATS);
  frames.forEach((f, i) => {
    const o = i * FRAME_FLOATS;
    out[o] = f.t;
    out[o + 1] = f.lm ? 1 : 0;
    if (f.lm) out.set(f.lm, o + 2);
  });
  return out;
}

export function deserializeFrames(data: Float32Array): PoseFrame[] {
  const n = Math.floor(data.length / FRAME_FLOATS);
  const frames: PoseFrame[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const o = i * FRAME_FLOATS;
    frames[i] = { t: data[o], lm: data[o + 1] ? data.slice(o + 2, o + FRAME_FLOATS) : null };
  }
  return frames;
}
